import {
  BatterReportChartConfigDto,
  BatterSessionReportChartConfigDto,
  ChartType,
  HeatmapChartConfigDto,
  PitcherReportChartConfigDto,
  PitcherSessionReportChartConfigDto,
  PitcherSingleSessionReportChartConfigDto,
} from 'types/CodegenReportTypes';

import { ChartConfigProps } from 'utilities/chartIdToComponent';
import GenericRadioConfig from 'components/Configs/GenericRadioConfig';
import { Typography } from '@trackman/web-shared-components';
import { useChartTitle } from 'hooks/helperHooks';
import { useTranslation } from 'react-i18next';

type ReportConfig =
  | BatterReportChartConfigDto
  | BatterSessionReportChartConfigDto
  | PitcherReportChartConfigDto
  | PitcherSessionReportChartConfigDto
  | PitcherSingleSessionReportChartConfigDto;

type Props<TReportConfig extends ReportConfig> = ChartConfigProps<HeatmapChartConfigDto, TReportConfig> & {
  chartId: ChartType;
  chartKey: keyof TReportConfig;
  lockDataView: boolean;
};

const StrikeZoneHeatmapChartSidebar = <T extends ReportConfig>({
  extraChartData,
  setTempLayout,
  tempLayout,
  chartId,
  chartKey,
  lockDataView,
}: Props<T>) => {
  const { getChartTitle } = useChartTitle();
  const { t } = useTranslation();

  let options = extraChartData.availableStats as string[];
  if (!lockDataView) {
    options = ['PitchLocation', ...options];
  }

  return (
    <div className='flex-column gap-2'>
      <Typography component='div' variant='body bold'>
        {getChartTitle(chartId as ChartType, extraChartData)}
      </Typography>
      <GenericRadioConfig
        label={t('View data as:')}
        options={options}
        selected={extraChartData.viewDataAs === 'PitchLocation' ? 'PitchLocation' : extraChartData.playerStat}
        setSelected={(selected) =>
          setTempLayout({
            ...tempLayout,
            [chartKey]: {
              ...tempLayout[chartKey],
              viewDataAs: selected === 'PitchLocation' ? 'PitchLocation' : 'PlayerStat',
              playerStat: selected === 'PitchLocation' ? tempLayout[chartKey]['playerStat'] : selected,
            },
          })
        }
        disabled={false}
      />
    </div>
  );
};

export const BatterStrikeZoneHeatmapChartSidebar = (props: ChartConfigProps<HeatmapChartConfigDto, BatterReportChartConfigDto>) => {
  return <StrikeZoneHeatmapChartSidebar chartId='BatterHeatmapChart' chartKey='batterHeatmapChartConfig' lockDataView={true} {...props} />;
};

export const BatterSessionStrikeZoneHeatmapChartSidebar = (
  props: ChartConfigProps<HeatmapChartConfigDto, BatterSessionReportChartConfigDto>
) => {
  return (
    <StrikeZoneHeatmapChartSidebar
      chartId='BatterSessionHeatmapChart'
      chartKey='batterSessionHeatmapChartConfig'
      lockDataView={true}
      {...props}
    />
  );
};

export const PitcherStrikeZoneHeatmapChartSidebar = (props: ChartConfigProps<HeatmapChartConfigDto, PitcherReportChartConfigDto>) => {
  return (
    <StrikeZoneHeatmapChartSidebar chartId='PitcherHeatmapChart' chartKey='pitcherHeatmapChartConfig' lockDataView={false} {...props} />
  );
};

export const PitcherSessionStrikeZoneHeatmapChartSidebar = (
  props: ChartConfigProps<HeatmapChartConfigDto, PitcherSessionReportChartConfigDto>
) => {
  return (
    <StrikeZoneHeatmapChartSidebar
      chartId='PitcherSessionHeatmapChart'
      chartKey='pitcherSessionHeatmapChartConfig'
      lockDataView={false}
      {...props}
    />
  );
};

export const PitcherSingleSessionStrikeZoneHeatmapChartSidebar = (
  props: ChartConfigProps<HeatmapChartConfigDto, PitcherSingleSessionReportChartConfigDto>
) => {
  return (
    <StrikeZoneHeatmapChartSidebar
      chartId='PitcherSingleSessionHeatmapChart'
      chartKey='pitcherSingleSessionHeatmapChartConfig'
      lockDataView={false}
      {...props}
    />
  );
};
